import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, FormBlock, TableBlock } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, pairsFrom, submitWrite, writeRefusal } from './writes'

/**
 * Content & Localization › Notification templates (`ct-templates`), read from
 * `GET /v1/admin/copy/entries` (go-wallet#155, FR-20.x).
 *
 * # Every sentence this product says, with any override laid over it
 *
 * `base` is what is compiled into the running build. `effective` is what a
 * customer reads NOW, "equal to `base` exactly when `overridden` is false". The
 * two are shown together and never collapsed: an override is a sentence
 * somebody published on top of the build, and a screen showing only the
 * effective text cannot answer "is this what we shipped, or did somebody change
 * it" — which is the question asked when a customer quotes something back.
 *
 * # `revision: 0` means nothing has ever been published
 *
 * Not "revision zero". It is the state of a deployment where the override layer
 * has never been written to, and a console rendering it as a number invites
 * somebody to go looking for it.
 *
 * # Placeholders bound what an override may say
 *
 * `placeholders` are "the `{name}` tokens the BASE sentence interpolates, and
 * therefore the only ones a published override may use". An override naming a
 * token the base does not carry cannot be filled, so they are rendered beside
 * the sentence rather than hidden — they are the constraint on the edit, and
 * the edit is somebody's next action.
 *
 * # Read-only
 *
 * Publishing an override is a revision — a versioned change to what customers
 * are told, in one transaction with its audit row. Not called here (law 6, law
 * 21). This screen answers what is said and whether it was overridden.
 */

export const COPY_ENTRIES_PATH = '/copy/entries'

const SOURCE = 'postgres · copy overrides over the running build (GET /v1/admin/copy/entries)'
const AUTHORITY = 'operational · what a customer reads now'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

interface Entry {
  readonly key: string
  readonly base: string
  readonly effective: string
  readonly overridden: boolean | null
  readonly placeholders: readonly string[]
  readonly version: number | null
  readonly publishedBy: string
}

function readEntry(raw: unknown): Entry | null {
  if (!isObject(raw)) return null
  const key = text(raw['key'])
  if (key === '') return null
  return {
    key,
    base: text(raw['base']),
    effective: text(raw['effective']),
    overridden: typeof raw['overridden'] === 'boolean' ? raw['overridden'] : null,
    placeholders: Array.isArray(raw['placeholders'])
      ? (raw['placeholders'] as readonly unknown[]).filter((p): p is string => typeof p === 'string')
      : [],
    version: whole(raw['version']),
    publishedBy: text(raw['published_by']),
  }
}

const COLS: readonly Column[] = [
  ['Key', 'm', 'minmax(200px,1.4fr)'],
  ['What the build says', 'x', 'minmax(240px,1.8fr)'],
  ['What a customer reads now', 'x', 'minmax(240px,1.8fr)'],
  ['Overridden', 'c', '170px'],
  ['Placeholders', 'm', 'minmax(150px,1.1fr)'],
  ['Published by', 'x', '150px'],
]

function row(e: Entry): readonly Cell[] {
  return [
    e.key,
    e.base === '' ? '— no base sentence' : e.base,
    // Never collapsed into one column. "Is this what we shipped, or did
    // somebody change it" is the question asked when a customer quotes
    // something back, and one column cannot answer it.
    e.effective === '' ? '— nothing effective' : e.effective,
    e.overridden === null
      ? (['— not stated', 'r'] as ChipCell)
      : e.overridden
        ? ([`overridden${e.version === null ? '' : ` · v${String(e.version)}`}`, 'a'] as ChipCell)
        : (['as built', 'g'] as ChipCell),
    // The only tokens a published override may use.
    e.placeholders.length === 0 ? '— none' : e.placeholders.join(' '),
    e.publishedBy === '' ? '— nobody' : e.publishedBy,
  ]
}

export function composeCopyEntries(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['entries']) ? (payload['entries'] as readonly unknown[]) : []
  const rows: Entry[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readEntry(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const language = text(payload['language'])
  const revision = whole(payload['revision'])
  const overlayError = text(payload['overlay_error'])
  const overridden = rows.filter((e) => e.overridden === true).length

  const table: TableBlock = {
    t: 'table',
    title: `Every sentence this product says${language === '' ? '' : ` · ${language}`}`,
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} sentence(s)${overridden === 0 ? '' : ` · ${String(overridden)} overridden`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}${revision === null ? '' : revision === 0 ? ' · nothing has ever been published' : ` · revision ${String(revision)}`}`,
    foot2:
      'The build’s sentence and the effective one are separate columns and are never collapsed: an override is something somebody published on top of the build, and one column cannot answer “is this what we shipped, or did somebody change it” — which is the question asked when a customer quotes something back at us. The placeholders are the only tokens a published override may use.',
  }

  const blocks: Block[] = [table]

  if (overlayError !== '') {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Overlay',
      title: 'The override layer could not be read',
      body: `${overlayError} What is shown is the build’s own sentences, which is what a customer reads only if nothing has been overridden — and this read cannot say whether anything has.`,
      ref: 'the base is the build’s; the effective column may be understating an override',
    })
  }

  if (revision === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Never published',
      title: 'Nothing has ever been published over this build',
      body: 'Every sentence a customer reads is the one compiled into the running image. That is a real state and not an empty screen — and it is the state with the fewest ways to be wrong.',
      ref: 'revision 0 · the override layer has never been written to',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'String catalogue',
      title: 'No sentence came back at all',
      body: 'The catalogue is what every screen in the apps renders from, so an empty read is not a product with nothing to say — it is a read that found no catalogue. The apps forbid rendering a key, so there is no benign version of this.',
      ref: 'the read returned no entries',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} sentence(s) could not be read`,
      body: 'Counted rather than dropped. A sentence missing from this screen is one nobody can check against what a customer quoted.',
      ref: 'an entry with no key names no sentence and is refused',
    })
  }

  const missing: string[] = []
  if (revision === null) missing.push('which revision of the override layer is live')
  if (overlayError !== '') missing.push('whether any of these sentences is overridden')

  return {
    blocks,
    provenance: missing.length === 0
      ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
      : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing },
    rowKeys: rows.map((e) => e.key),
  }
}


export const COPY_REVISIONS_PATH = '/copy/revisions'

const REVISION_COLS: readonly Column[] = [
  ['Revision', 'm', '100px'],
  ['Language', 'm', '100px'],
  ['Published', 'm', '130px'],
  ['By', 'x', '150px'],
  ['Reason', 'x', 'minmax(190px,1.3fr)'],
  ['Sentences opened / closed', 'n', '190px'],
  ['Written against', 'c', 'minmax(190px,1.3fr)'],
]

/**
 * One publication of the override layer.
 *
 * `base_matches_running_build` is the row that matters. A revision published
 * against a build that is no longer deployed was written over sentences this
 * image may not carry — the override still applies where the key exists, and
 * where it does not, somebody edited text nobody is serving. That is invisible
 * unless the revision says which build it was written against.
 */
function revisionRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const revision = whole(raw['revision'])
  if (revision === null) return null
  const matches = typeof raw['base_matches_running_build'] === 'boolean'
    ? raw['base_matches_running_build']
    : null
  const opened = whole(raw['entries_opened'])
  const closed = whole(raw['entries_closed'])
  return [
    String(revision),
    text(raw['language']) === '' ? '— not stated' : text(raw['language']),
    text(raw['published_at']) === '' ? '— not returned' : text(raw['published_at']).slice(0, 10),
    text(raw['published_by']) === '' ? '— unattributed' : text(raw['published_by']),
    `${text(raw['reason_code']) === '' ? '— no code' : text(raw['reason_code'])}${text(raw['reason']) === '' ? '' : ` · ${text(raw['reason'])}`}`,
    `${opened === null ? '—' : String(opened)} / ${closed === null ? '—' : String(closed)}`,
    matches === null
      ? (['— not stated', 'r'] as ChipCell)
      : matches
        ? (['this build', 'g'] as ChipCell)
        : (['A BUILD NO LONGER DEPLOYED', 'a'] as ChipCell),
  ]
}

async function revisions(api: import('@/lib/api').ApiClient): Promise<readonly unknown[] | null> {
  try {
    const payload = await api.get<unknown>(COPY_REVISIONS_PATH)
    if (!isObject(payload)) return null
    const list = payload['revisions']
    return Array.isArray(list) ? (list as readonly unknown[]) : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
}

export const loadCopyEntries: ScreenLoader = async (api) => {
  let payload: unknown
  // Read beside the catalogue and never left dangling — see `callerBeside`.
  const who = callerBeside(api)
  try {
    payload = await api.get<unknown>(COPY_ENTRIES_PATH)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const reason = error instanceof ApiError
      ? `The catalogue endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
      : 'The backend could not be reached.'
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'String catalogue',
        title: 'What this product says could not be read',
        body: `${reason} Neither the build’s sentences nor any override over them is known — and an unread catalogue is not a product with nothing to say.`,
        ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : 'Nothing was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason },
    }
  }
  if (!isObject(payload)) {
    const reason = 'The catalogue endpoint returned a shape this console does not know.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'String catalogue', title: 'What this product says could not be read', body: reason, ref: 'Nothing was read.' }],
      provenance: { kind: 'unread', source: SOURCE, reason },
    }
  }
  const data = composeCopyEntries(payload, new Date())

  // The publish history, read after the catalogue and failing independently:
  // somebody who came to read what the product says should still read it when
  // the history of how it got that way does not load.
  const history = await revisions(api)
  const controls = copyControls(languagesIn(payload))(api, await who)

  if (history === null) {
    return {
      ...data,
      blocks: [...data.blocks, ...controls],
      provenance: {
        kind: 'partial',
        source: SOURCE,
        authority: AUTHORITY,
        readAt: readAtStamp(new Date()),
        missing: ['who published which override, and when'],
      },
    }
  }

  const rows: (readonly Cell[])[] = []
  let bad = 0
  let stale = 0
  for (const entry of history) {
    const parsed = revisionRow(entry)
    if (parsed === null) { bad += 1; continue }
    if (JSON.stringify(parsed[6]).includes('NO LONGER DEPLOYED')) stale += 1
    rows.push(parsed)
  }

  const blocks: Block[] = [...data.blocks]
  if (rows.length > 0 || bad > 0) {
    blocks.push({
      t: 'table',
      title: 'How the sentences got this way',
      src: 'ledger',
      facets: false,
      cols: REVISION_COLS,
      rows,
      footer: `${String(rows.length)} revision(s)${stale === 0 ? '' : ` · ${String(stale)} written against a build no longer deployed`}${bad === 0 ? '' : ` · ${String(bad)} could not be read`}`,
      foot2:
        'A revision published against a build that is no longer deployed was written over sentences this image may not carry: the override still applies where the key exists, and where it does not, somebody edited text nobody is serving. That is invisible unless the revision says which build it was written against.',
    })
  }

  if (stale > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Written against another build',
      title: `${String(stale)} revision(s) were published over a build that is not this one`,
      body: 'Their overrides still apply to every key this image carries. For any key it does not, somebody wrote a sentence nobody is being shown — which is not an error, and is worth knowing before somebody wonders why an edit had no effect.',
      ref: 'the running build’s own version is what the catalogue above is laid over',
    })
  }

  return { ...data, blocks: [...blocks, ...controls] }
}

/**
 * The languages the catalogue reported, for the publish control's picker.
 *
 * An empty list leaves the field a free-text tag rather than an empty select:
 * a picker with no options is a control an operator cannot use and cannot see
 * why, and the endpoint accepts any BCP-47 tag regardless of what this read
 * happened to mention.
 */
function languagesIn(payload: Record<string, unknown>): readonly string[] {
  const raw = payload['languages']
  if (!Array.isArray(raw)) return []
  return raw.filter((l): l is string => typeof l === 'string' && l !== '')
}

// ---------------------------------------------------------------------------
// One key's sentence history — GET /v1/admin/copy/entries/{copyKey}/versions
// ---------------------------------------------------------------------------

/**
 * Every sentence one key has ever had.
 *
 * A different shape from the help-article history and deliberately not folded
 * into it: an override has no review state, and it closes rather than being
 * superseded by a review verdict. What it has instead is a CLOSURE — who ended
 * it and why — and that is the half a catalogue cannot show.
 *
 * `known_to_build: false` is the row that earns this screen. "The history is
 * still real; nothing renders it any more." A key the running image no longer
 * carries still has everything ever published against it, and somebody looking
 * for why a sentence vanished needs to be told the build dropped the key rather
 * than that somebody closed the override.
 */
const KEY_HISTORY_COLS: readonly Column[] = [
  ['Version', 'm', '90px'],
  ['Language', 'm', '100px'],
  ['The sentence', 'x', 'minmax(260px,2fr)'],
  ['Effective', 'm', '190px'],
  ['Published by', 'x', '150px'],
  ['Why it ended', 'x', 'minmax(190px,1.3fr)'],
]

function keyVersionRow(raw: unknown): readonly Cell[] | null {
  if (!isObject(raw)) return null
  const version = whole(raw['version'])
  if (version === null) return null
  const to = text(raw['effective_to'])
  const closeCode = text(raw['close_reason_code'])
  return [
    `v${String(version)}`,
    text(raw['language']) === '' ? '— not stated' : text(raw['language']),
    text(raw['body']) === '' ? '— empty' : text(raw['body']),
    `${text(raw['effective_from']).slice(0, 10) === '' ? '—' : text(raw['effective_from']).slice(0, 10)} → ${to === '' ? 'open' : to.slice(0, 10)}`,
    `${text(raw['published_by']) === '' ? '— unattributed' : text(raw['published_by'])}${text(raw['reason_code']) === '' ? '' : ` · ${text(raw['reason_code'])}`}`,
    to === ''
      ? 'still in force'
      : `${text(raw['closed_by']) === '' ? '— unattributed' : text(raw['closed_by'])}${closeCode === '' ? '' : ` · ${closeCode}`}${text(raw['close_reason']) === '' ? '' : ` · ${text(raw['close_reason'])}`}`,
  ]
}

export async function readCopyKeyHistory(
  api: import('@/lib/api').ApiClient,
  copyKey: string,
): Promise<ScreenData> {
  const wanted = copyKey.trim()
  const source = 'postgres · copy override versions (GET /v1/admin/copy/entries/{copyKey}/versions)'
  if (wanted === '') {
    const reason = 'No key was given, so no history was looked up.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'Sentence history', title: 'No key was requested', body: reason, ref: 'a row with no key names no sentence' }],
      provenance: { kind: 'unread', source, reason },
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(`${COPY_ENTRIES_PATH}/${encodeURIComponent(wanted)}/versions`)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const reason = error instanceof ApiError
      ? `The history endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
      : 'The backend could not be reached.'
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Sentence history',
        title: `The history of ${wanted} could not be read`,
        body: `${reason} What this key has said before, and who ended each version, are unknown from this read.`,
        ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : `key asked for: ${wanted}`,
      }],
      provenance: { kind: 'unread', source, reason },
    }
  }

  const o = isObject(payload) ? payload : {}
  const known = typeof o['known_to_build'] === 'boolean' ? o['known_to_build'] : null
  const raw = Array.isArray(o['versions']) ? (o['versions'] as readonly unknown[]) : []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = keyVersionRow(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [{
    t: 'table',
    title: `${wanted} · every sentence it has had`,
    src: 'ledger',
    facets: false,
    cols: KEY_HISTORY_COLS,
    rows,
    footer: `${String(rows.length)} version(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'An override is closed rather than edited, so every version here says who ended it and why — which is the half a catalogue of current sentences cannot show. The base sentence beneath them all is the build’s, and it is not in this list because nobody published it.',
  }]

  if (known === false) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'The build dropped this key',
      title: `The running build no longer carries ${wanted}`,
      body: 'The history above is still real; nothing renders it any more. Somebody looking for why a sentence disappeared should be told the build stopped carrying the key rather than that an override was closed — those have different remedies, and only one of them is in this console.',
      ref: 'known_to_build = false',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'n',
      kicker: 'Never overridden',
      title: `Nothing has ever been published over ${wanted}`,
      body: 'A customer reads the sentence compiled into the build, and always has. That is the state with the fewest ways to be wrong.',
      ref: `${wanted} · no override version`,
    })
  }

  return { blocks, provenance: { kind: 'live', source, authority: AUTHORITY, readAt: readAtStamp(new Date()) } }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Reading the catalogue is Support's; changing what customers read is Ops's. */
const PERM_COPY_WRITE = 'copy.write'

/**
 * Why a sentence is being changed, as a code the column will accept.
 *
 * Matched by the backend against `^[a-z][a-z0-9_]{1,39}$`, and it is the
 * MACHINE-READABLE half of the attribution. The sentence half — "support ticket
 * 4412: customers read 'pending' as 'failed'" — goes in the reason note beside
 * it, which is where an operator's own words belong.
 */
const COPY_REASONS: readonly (readonly [string, string, string])[] = [
  ['support_confusion', 'Customers misread the current wording', ''],
  ['factually_wrong', 'The sentence states something untrue', ''],
  ['legal_required', 'Required by legal or a regulator', ''],
  ['product_change', 'The product changed under the words', ''],
  ['tone', 'House style, meaning unchanged', ''],
  ['typo', 'A typo or a punctuation fix', ''],
]

/**
 * Publishing a copy revision.
 *
 * # The set is the unit of work, and the control takes a set
 *
 * `POST /copy/entries` publishes several entries as ONE revision, because copy
 * is edited in sentences that belong together — a refusal and the sentence
 * explaining what to do next are two entries and one editorial decision.
 * Publishing them separately would give them two revisions, and a refusal
 * rendered in between would cite a revision in which half the change existed.
 * A form offering one key at a time would make that the default behaviour of
 * the control, so the field is a repeating list.
 *
 * # `wording_only` is asked as a positive, and that is deliberate
 *
 * It is the publisher SAYING the meaning did not move. The backend spells the
 * column this way round on purpose: the natural name is `meaning_changed`,
 * whose zero value is `false` — the fail-OPEN direction on the one flag that
 * decides whether every translation of a legal sentence has to be re-read. An
 * omitted field would assert the meaning held, about an edit nobody looked at.
 *
 * So the checkbox starts UNTICKED and unticked means "the meaning moved". The
 * cost of leaving it alone is a re-translation cycle nobody needed; the cost of
 * the other default is a stale legal disclosure nobody noticed.
 *
 * # Not `versioned`, though it publishes a revision
 *
 * Law 6's control supersedes a live row on an effective date. A copy revision
 * takes effect when it is written — there is no future boundary to pick, and
 * the endpoint accepts none. Rendering an `effective_from` here would offer a
 * choice the server ignores, which is worse than not offering it.
 */
function publishCopyForm(api: ApiClient, caller: Caller, languages: readonly string[]): FormBlock {
  const blocked = writeRefusal(
    caller, PERM_COPY_WRITE, 'Ops', 'Publishing what customers read',
  )
  return {
    t: 'form',
    title: 'Publish an override',
    lead: 'Lays an override over what the running build says. Everything below publishes as one revision, so sentences that belong together cannot end up citing different ones.',
    fields: [
      {
        name: 'language', label: 'Language', kind: languages.length === 0 ? 'text' : 'select',
        required: true, mono: true,
        ...(languages.length === 0
          ? { placeholder: 'en', note: 'A BCP-47 tag.' }
          : { options: languages.map((l) => [l, l] as const) }),
      },
      {
        name: 'entries', label: 'Sentences', kind: 'pairs', required: true,
        pair: { keyLabel: 'Key', keyName: 'key', valueLabel: 'What the customer reads', valueName: 'body' },
      },
      {
        name: 'wording_only', label: 'Wording only — the meaning is unchanged',
        kind: 'checkbox', required: false,
        note: 'Leave this alone if the meaning moved at all. Unticked means every translation of these sentences is re-read; ticked means none is, so tick it only for a comma, a typo or house style.',
      },
    ],
    reasons: COPY_REASONS,
    reasonNote: 'The code goes on the revision. Put the ticket or the sentence in the note beside it.',
    auditLine: 'admin.copy.publish · actor · copy_revision · reason_code + reason',
    cta: 'Publish revision',
    ctaNote: 'Takes effect for customers as soon as it is written.',
    blocked,
    ...(blocked === '' ? {
      submit: (v) => submitWrite(
        () => api.post<unknown>(COPY_ENTRIES_PATH, {
          language: v['language'],
          reason_code: v['reason'],
          // Sent as the publisher's own assertion. Absent means the meaning
          // moved, which is the direction that costs a re-read rather than a
          // stale disclosure.
          wording_only: v['wording_only'] === 'true',
          entries: pairsFrom(v['entries'], 'key', 'body'),
          reason: v['reason_note'] ?? v['reason'],
        }),
        'The revision was published',
        (answer) => (isObject(answer) && typeof answer['revision'] === 'number'
          ? [['revision', String(answer['revision']), 'what a later refusal will cite']]
          : []),
      ),
    } : {}),
  }
}

/** The control this screen offers, given who is asking. */
export function copyControls(languages: readonly string[]) {
  return (api: ApiClient, caller: Caller): readonly Block[] => [
    publishCopyForm(api, caller, languages),
  ]
}
